import React, { memo, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, Alert } from 'react-native';
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  type SharedValue,
} from 'react-native-reanimated';
import { useTranslation } from '../../shared/i18n';
import { isRTL, rtlWritingDirection } from '../../shared/i18n/rtl';
import { Clickable2, useClickable2Press } from '../../components/common/Clickable2';
import { SvgIcon } from '../../components/common/SvgIcon';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { centeredLabelText } from '../../shared/theme/sharedStyles';
import { MenuFlowCounter } from './MenuFlowCounter';

export const MENU_FLOW_COUNTERS_BLOCK_HEIGHT = 38;

export type MenuFlowCountersRowProps = {
  mealsCount: number;
  snacksCount: number;
  selectedMealsCount: number;
  selectedSnacksCount: number;
  isDoubleBoxForDate?: boolean;
  activeBox?: 'box1' | 'box2';
  onBoxSelect?: (box: 'box1' | 'box2') => void;
  onCancelBox?: () => void;
  onRequestDoubleBox?: () => void;
};

/**
 * Nested radii: header stagger 16 + 4px pad → content ~12.
 * Track chrome lives on the animated clip view so width + radius share one box (no shear).
 */
const SEGMENT_R = appTheme.borderRadius.pill;
const TAB_R = appTheme.borderRadius.pill;
const CLOSE_R = appTheme.borderRadius.pill;
const TAB_H = 24;
const TAB_IDLE_W = 52;
const TAB_ACTIVE_W = 78;
const CLOSE_SIZE = 16;
const TAB_GAP = 2;
const SEGMENT_PAD = 2;
const LEADING_INNER_H = TAB_H + SEGMENT_PAD * 2;
const LEADING_W = SEGMENT_PAD * 2 + TAB_IDLE_W + TAB_ACTIVE_W + TAB_GAP;
const CTA_W = 88;
const MODE_SPRING = { stiffness: 260, damping: 26, mass: 0.55 };
const TAB_SPRING = { stiffness: 340, damping: 28, mass: 0.42 };

function createStyles(c: ThemeColors) {
  const rtl = isRTL();
  return StyleSheet.create({
    /** Layout only — no overflow clip (would shear the box track corners). */
    shell: {
      flex: 1,
      minHeight: 0,
      flexDirection: 'row',
      alignItems: 'center',
    },
    /**
     * Animated width + track chrome are the same view so corners stay pixel-aligned
     * while the slot opens/closes.
     */
    slotTrack: {
      height: LEADING_INNER_H,
      borderRadius: SEGMENT_R,
      backgroundColor: c.surface,
      borderWidth: 0,
      borderColor: c.border,
      overflow: 'hidden',
    },
    leadingInner: {
      width: LEADING_W,
      height: LEADING_INNER_H,
      flexDirection: 'row',
      alignItems: 'center',
      gap: TAB_GAP,
      padding: SEGMENT_PAD,
    },
    mid: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
    },
    midSpacer: {
      flex: 1,
      minWidth: 8,
    },
    trailingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexShrink: 0,
      gap: TAB_GAP,
      marginStart: 4,
    },
    /** Mirrors slotTrack — one nest per counter for symmetry with box tabs. */
    counterNest: {
      height: LEADING_INNER_H,
      borderRadius: SEGMENT_R,
      backgroundColor: c.surface,
      borderWidth: 0,
      borderColor: c.border,
      paddingHorizontal: 8,
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
    },
    counterNestActive: {
      borderColor: c.primary,
    },
    counterPad: {
      flex: 1,
      minWidth: 0,
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingHorizontal: 4,
    },
    counterPadEnd: {
      alignItems: 'flex-end',
    },
    centerCtaClip: {
      overflow: 'hidden',
      alignItems: 'center',
      justifyContent: 'center',
    },
    ctaInner: {
      width: CTA_W,
      alignItems: 'center',
      justifyContent: 'center',
    },
    tabClip: {
      height: TAB_H,
      overflow: 'hidden',
      borderRadius: TAB_R,
    },
    tabRow: {
      height: TAB_H,
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 6,
      borderRadius: TAB_R,
    },
    tabRowActive: {
      backgroundColor: c.primary,
      paddingHorizontal: 5,
      gap: 4,
    },
    tabSelectHit: {
      flex: 1,
      height: '100%',
      minWidth: 0,
      justifyContent: 'center',
    },
    tabSelectHitIdle: {
      alignItems: 'center',
    },
    tabSelectHitActive: {
      alignItems: 'center',
    },
    tabLabel: {
      ...centeredLabelText,
      width: '100%',
      fontSize: 10,
      lineHeight: 13,
      fontWeight: '600',
      letterSpacing: rtl ? 0 : 0.1,
      color: c.text,
      writingDirection: rtlWritingDirection(),
    },
    tabLabelActive: {
      color: c.onPrimary,
      textAlign: 'center',
    },
    closeHit: {
      width: CLOSE_SIZE,
      height: CLOSE_SIZE,
      borderRadius: CLOSE_R,
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.errorLight,
    },
    addBtn: {
      height: 24,
      paddingHorizontal: 8,
      borderRadius: TAB_R,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      maxWidth: CTA_W,
    },
    addLabel: {
      ...centeredLabelText,
      fontSize: 11,
      lineHeight: 14,
      fontWeight: '600',
      letterSpacing: rtl ? 0 : 0.1,
      color: c.primary,
      writingDirection: rtlWritingDirection(),
    },
  });
}

type Styles = ReturnType<typeof createStyles>;

function PressScale({
  children,
  fill,
  align = 'center',
}: {
  children: React.ReactNode;
  fill?: boolean;
  align?: 'center' | 'flex-start';
}) {
  const { pressed } = useClickable2Press();
  const anim = useAnimatedStyle(() => ({
    transform: [{ scale: interpolate(pressed.value, [0, 1], [1, 0.94]) }],
  }));
  return (
    <Animated.View
      style={[
        fill ? { flex: 1, width: '100%', alignItems: align, justifyContent: 'center' } : null,
        anim,
      ]}>
      {children}
    </Animated.View>
  );
}

function confirmCancelBox(onCancelBox: () => void, t: (key: string) => string) {
  Alert.alert(t('menu.cancelBoxTitle'), t('menu.cancelBoxMessage'), [
    { text: t('menu.cancelBoxConfirm'), onPress: () => onCancelBox() },
    { text: t('common.cancel'), style: 'cancel' },
  ]);
}

function confirmEnableDoubleBox(
  onRequestDoubleBox: () => void,
  t: (key: string) => string,
) {
  Alert.alert(t('menu.enableDoubleBoxTitle'), t('menu.enableDoubleBoxMessage'), [
    {
      text: t('menu.enableDoubleBoxConfirm'),
      onPress: () => {
        onRequestDoubleBox();
      },
    },
    { text: t('common.cancel'), style: 'cancel' },
  ]);
}

const BoxTab = memo(function BoxTab({
  active,
  label,
  onPress,
  onCancel,
  widthSV,
  styles,
  closeColor,
  t,
}: {
  active: boolean;
  label: string;
  onPress: () => void;
  onCancel?: () => void;
  widthSV: SharedValue<number>;
  styles: Styles;
  closeColor: string;
  t: (key: string) => string;
}) {
  const clip_style = useAnimatedStyle(() => ({
    width: widthSV.value,
  }));

  return (
    <Animated.View style={[styles.tabClip, clip_style]}>
      <View
        style={[
          styles.tabRow,
          active ? styles.tabRowActive : null,
          { width: active ? TAB_ACTIVE_W : TAB_IDLE_W },
        ]}>
        <Clickable2
          onPress={onPress}
          style={[
            styles.tabSelectHit,
            active ? styles.tabSelectHitActive : styles.tabSelectHitIdle,
          ]}
          accessibilityLabel={label}
          accessibilityRole="tab"
          accessibilityState={{ selected: active }}
          haptics
          hapticType="selection">
          <PressScale fill align="center">
            <Text
              style={[styles.tabLabel, active && styles.tabLabelActive]}
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.85}
              allowFontScaling={false}>
              {label}
            </Text>
          </PressScale>
        </Clickable2>
        {active && onCancel ? (
          <Clickable2
            onPress={() => confirmCancelBox(onCancel, t)}
            style={styles.closeHit}
            accessibilityLabel={t('menu.cancelBoxTitle')}
            accessibilityRole="button"
            hitSlop={16}
            haptics
            hapticType="soft">
            <PressScale>
              <SvgIcon name="x" size={12} color={closeColor} />
            </PressScale>
          </Clickable2>
        ) : null}
      </View>
    </Animated.View>
  );
});

function MenuFlowCountersRowComponent({
  mealsCount,
  snacksCount,
  selectedMealsCount,
  selectedSnacksCount,
  isDoubleBoxForDate = false,
  activeBox = 'box1',
  onBoxSelect,
  onCancelBox,
  onRequestDoubleBox,
}: MenuFlowCountersRowProps) {
  const { t } = useTranslation();
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createStyles(themeColors), [themeColors]);
  const close_color = themeColors.errorText;

  const can_open_tabs = Boolean(onBoxSelect);
  const can_add = Boolean(onRequestDoubleBox);
  const mode_target = isDoubleBoxForDate && can_open_tabs ? 1 : 0;

  const mode = useSharedValue(mode_target);
  const box1_w = useSharedValue(activeBox === 'box1' ? TAB_ACTIVE_W : TAB_IDLE_W);
  const box2_w = useSharedValue(activeBox === 'box2' ? TAB_ACTIVE_W : TAB_IDLE_W);

  useEffect(() => {
    mode.value = withSpring(mode_target, MODE_SPRING);
  }, [mode, mode_target]);

  useEffect(() => {
    box1_w.value = withSpring(activeBox === 'box1' ? TAB_ACTIVE_W : TAB_IDLE_W, TAB_SPRING);
    box2_w.value = withSpring(activeBox === 'box2' ? TAB_ACTIVE_W : TAB_IDLE_W, TAB_SPRING);
  }, [activeBox, box1_w, box2_w]);

  const leading_style = useAnimatedStyle(() => ({
    width: interpolate(mode.value, [0, 1], [0, LEADING_W], Extrapolation.CLAMP),
    opacity: interpolate(mode.value, [0, 0.35, 1], [0, 0, 1], Extrapolation.CLAMP),
  }));

  const trailing_style = useAnimatedStyle(() => ({
    opacity: interpolate(mode.value, [0, 0.35, 1], [0, 0, 1], Extrapolation.CLAMP),
    transform: [{ scale: interpolate(mode.value, [0, 1], [0.94, 1], Extrapolation.CLAMP) }],
  }));

  const cta_style = useAnimatedStyle(() => ({
    width: interpolate(mode.value, [0, 1], [can_add ? CTA_W : 0, 0], Extrapolation.CLAMP),
    opacity: interpolate(mode.value, [0, 0.55, 1], [can_add ? 1 : 0, 0, 0], Extrapolation.CLAMP),
  }));

  const tabs_interactive = mode_target === 1;
  const cta_interactive = mode_target === 0 && can_add;

  return (
    <View style={styles.shell}>
      <Animated.View style={[styles.slotTrack, leading_style]} pointerEvents={tabs_interactive ? 'auto' : 'none'}>
        <View style={styles.leadingInner}>
          {can_open_tabs && onBoxSelect ? (
            <>
              <BoxTab
                active={activeBox === 'box1'}
                label={t('menu.box1')}
                onPress={() => onBoxSelect('box1')}
                onCancel={onCancelBox}
                widthSV={box1_w}
                styles={styles}
                closeColor={close_color}
                t={t}
              />
              <BoxTab
                active={activeBox === 'box2'}
                label={t('menu.box2')}
                onPress={() => onBoxSelect('box2')}
                onCancel={onCancelBox}
                widthSV={box2_w}
                styles={styles}
                closeColor={close_color}
                t={t}
              />
            </>
          ) : null}
        </View>
      </Animated.View>

      {tabs_interactive ? (
        <>
          <View style={styles.midSpacer} />
          <Animated.View
            style={[styles.trailingRow, trailing_style]}
            pointerEvents="auto">
            <View
              style={[
                styles.counterNest,
                activeBox === 'box1' ? styles.counterNestActive : null,
              ]}>
              <MenuFlowCounter
                bare
                label={t('subscriptions.meals')}
                count={mealsCount}
                selectedCount={selectedMealsCount}
                isActive={activeBox === 'box1'}
              />
            </View>
            <View
              style={[
                styles.counterNest,
                activeBox === 'box2' ? styles.counterNestActive : null,
              ]}>
              <MenuFlowCounter
                bare
                label={t('subscriptions.snacks')}
                count={snacksCount}
                selectedCount={selectedSnacksCount}
                isActive={activeBox === 'box2'}
              />
            </View>
          </Animated.View>
        </>
      ) : (
        <View style={styles.mid}>
          <View style={styles.counterPad}>
            <MenuFlowCounter
              bare
              label={t('subscriptions.meals')}
              count={mealsCount}
              selectedCount={selectedMealsCount}
            />
          </View>

          <Animated.View style={[styles.centerCtaClip, cta_style]} pointerEvents={cta_interactive ? 'auto' : 'none'}>
            <View style={styles.ctaInner}>
              {can_add && onRequestDoubleBox ? (
                <Clickable2
                  onPress={() =>
                    confirmEnableDoubleBox(onRequestDoubleBox, t)
                  }
                  style={styles.addBtn}
                  accessibilityLabel={t('menu.addSecondBoxHint')}
                  accessibilityRole="button"
                  haptics
                  hapticType="soft">
                  <PressScale>
                    <Text
                      style={styles.addLabel}
                      numberOfLines={1}
                      adjustsFontSizeToFit
                      minimumFontScale={0.8}
                      allowFontScaling={false}>
                      {t('menu.doubleBox')}
                    </Text>
                  </PressScale>
                </Clickable2>
              ) : null}
            </View>
          </Animated.View>

          <View style={[styles.counterPad, styles.counterPadEnd]}>
            <MenuFlowCounter
              bare
              label={t('subscriptions.snacks')}
              count={snacksCount}
              selectedCount={selectedSnacksCount}
            />
          </View>
        </View>
      )}
    </View>
  );
}

export const MenuFlowCountersRow = memo(MenuFlowCountersRowComponent);
